// ========================================
// Intelligence Hub - Unified Insights Modal
// ========================================
import React, { useState } from 'react';
import {
    X, Sparkles, MessageCircle, Share2, ArrowLeft,
    PackagePlus, PackageX, TrendingUp, TrendingDown,
    AlertTriangle, CheckCircle, ArrowUpRight, ArrowDownRight
} from 'lucide-react';
import { ALERT_TYPES, ALERT_CONFIG } from '../utils/briefingConstants';

// מיפוי אייקונים
const IconMap = {
    PackagePlus,
    PackageX,
    TrendingUp,
    TrendingDown,
    AlertTriangle,
};

// --- Tab Button ---
const TabButton = ({ label, active, onClick, count, icon: Icon }) => (
    <button
        onClick={onClick}
        className={`
            flex-1 flex items-center justify-center gap-2 py-3 px-4 text-sm font-bold transition-all
            ${active
                ? 'bg-white text-slate-900 shadow-md rounded-xl'
                : 'text-slate-400 hover:text-slate-200'
            }
        `}
    >
        {Icon && <Icon size={16} />}
        {label}
        {count > 0 && (
            <span className={`text-xs px-1.5 py-0.5 rounded-full ${active ? 'bg-slate-100' : 'bg-white/20'}`}>
                {count}
            </span>
        )}
    </button>
);

// --- Morning Briefing Alert Card ---
const BriefingAlertCard = ({ alert }) => {
    const config = ALERT_CONFIG[alert.type];
    const Icon = IconMap[config?.icon] || AlertTriangle;

    return (
        <div className={`flex items-center gap-3 p-3 rounded-xl border ${config?.bgClass || 'bg-slate-50 border-slate-200'} transition-all hover:scale-[1.01]`} dir="rtl">
            <div className={`p-2 rounded-lg bg-white/80 ${config?.iconClass || 'text-slate-600'}`}>
                <Icon size={20} />
            </div>
            <div className="flex-1 min-w-0">
                <p className={`text-sm font-bold ${config?.textClass || 'text-slate-900'} truncate`}>
                    {alert.description}
                </p>
                <p className="text-xs text-slate-500 font-mono">{alert.sku}</p>
            </div>
            <div className="text-left">
                <span className={`text-xs font-bold ${config?.textClass || 'text-slate-600'} px-2 py-1 rounded-full bg-white/50`}>
                    {config?.title || 'שינוי'}
                </span>
            </div>
        </div>
    );
};

// --- AI Insights Alert Card ---
const InsightAlertCard = ({ insight }) => {
    const typeStyles = {
        RESTOCKED: { bg: 'bg-green-50 border-green-100', icon: CheckCircle, iconClass: 'text-green-600', textClass: 'text-green-800' },
        STOCKOUT: { bg: 'bg-red-50 border-red-100', icon: AlertTriangle, iconClass: 'text-red-600', textClass: 'text-red-800' },
        LOW_STOCK: { bg: 'bg-amber-50 border-amber-100', icon: ArrowDownRight, iconClass: 'text-amber-600', textClass: 'text-amber-800' },
        CLASS_UP: { bg: 'bg-blue-50 border-blue-100', icon: ArrowUpRight, iconClass: 'text-blue-600', textClass: 'text-blue-800' },
    };

    const style = typeStyles[insight.type] || typeStyles.LOW_STOCK;
    const Icon = style.icon;

    return (
        <div className={`flex items-center gap-3 p-3 rounded-xl border ${style.bg} transition-all hover:scale-[1.01]`} dir="rtl">
            <div className={`p-2 rounded-lg bg-white/80 ${style.iconClass}`}>
                <Icon size={20} />
            </div>
            <div className="flex-1 min-w-0">
                <p className={`text-sm font-bold ${style.textClass} truncate`}>
                    {insight.message}
                </p>
                <p className="text-xs text-slate-500 font-mono">{insight.sku}</p>
            </div>
            <div className="text-left">
                <span className="text-xs font-mono font-bold bg-white/50 text-slate-600 px-2 py-1 rounded">
                    {insight.details}
                </span>
            </div>
        </div>
    );
};

// --- Main Intelligence Hub Modal ---
const IntelligenceHub = ({
    isOpen,
    onClose,
    // Morning Briefing data
    morningAlerts = [],
    morningSummary = {},
    // AI Insights data
    insights = [],
    onGenerateReport
}) => {
    // הטאב הפעיל - אם יש התראות תדריך בוקר, נפתח בו. אחרת בתובנות.
    const defaultTab = morningAlerts.length > 0 ? 'briefing' : 'insights';
    const [activeTab, setActiveTab] = useState(defaultTab);

    if (!isOpen) return null;

    const hasBriefingAlerts = morningAlerts.length > 0;
    const hasInsights = insights && insights.length > 0;
    const hasAnyContent = hasBriefingAlerts || hasInsights;

    // קיבוץ התראות תדריך לפי סוג
    const groupedBriefingAlerts = {
        [ALERT_TYPES.STOCKOUT]: morningAlerts.filter(a => a.type === ALERT_TYPES.STOCKOUT),
        [ALERT_TYPES.RESTOCK]: morningAlerts.filter(a => a.type === ALERT_TYPES.RESTOCK),
        [ALERT_TYPES.TRENDING_UP]: morningAlerts.filter(a => a.type === ALERT_TYPES.TRENDING_UP),
        [ALERT_TYPES.LOW_STOCK]: morningAlerts.filter(a => a.type === ALERT_TYPES.LOW_STOCK),
    };

    const handleShareRestock = () => {
        const restockedItems = groupedBriefingAlerts[ALERT_TYPES.RESTOCK];
        if (!restockedItems?.length) return;

        const itemsList = restockedItems
            .map(item => `• מק"ט: ${item.sku} | כמות: ${item.currentValue}`)
            .join('\n');
        const message = `✅ *עדכון מלאי AEG*\nהפריטים הבאים חזרו למלאי:\n\n${itemsList}`;
        window.open(`https://wa.me/?text=${encodeURIComponent(message)}`, '_blank');
    };

    const handleGenerateReport = () => {
        if (onGenerateReport && insights) {
            onGenerateReport(insights.map(i => i.sku));
        }
    };

    return (
        <div className="fixed inset-0 z-50 flex items-end md:items-center justify-center">
            {/* רקע כהה */}
            <div
                className="absolute inset-0 bg-slate-900/60 backdrop-blur-sm"
                onClick={onClose}
            />

            {/* המודל */}
            <div className="relative w-full max-w-lg max-h-[90vh] bg-white rounded-t-3xl md:rounded-3xl shadow-2xl overflow-hidden animate-fade-in-up flex flex-col" dir="rtl">

                {/* כותרת */}
                <div className="sticky top-0 z-10 bg-gradient-to-b from-slate-900 to-slate-800 text-white p-6 pb-4">
                    <div className="flex items-center justify-between mb-4">
                        <div className="flex items-center gap-3">
                            <div className="p-2 bg-white/10 rounded-xl">
                                <Sparkles className="text-amber-400" size={24} />
                            </div>
                            <div>
                                <h2 className="text-xl font-bold">מרכז המודיעין</h2>
                                <p className="text-slate-400 text-sm">תובנות ושינויים במלאי</p>
                            </div>
                        </div>
                        <button
                            onClick={onClose}
                            className="p-2 hover:bg-white/10 rounded-xl transition-colors"
                        >
                            <X size={24} />
                        </button>
                    </div>

                    {/* טאבים */}
                    <div className="flex gap-2 p-1 bg-white/10 rounded-xl">
                        <TabButton
                            label="תדריך בוקר"
                            active={activeTab === 'briefing'}
                            onClick={() => setActiveTab('briefing')}
                            count={morningAlerts.length}
                            icon={PackagePlus}
                        />
                        <TabButton
                            label="שינויים אחרונים"
                            active={activeTab === 'insights'}
                            onClick={() => setActiveTab('insights')}
                            count={insights?.length || 0}
                            icon={TrendingUp}
                        />
                    </div>
                </div>

                {/* תוכן */}
                <div className="flex-1 overflow-y-auto p-6 space-y-4 custom-scrollbar">
                    {!hasAnyContent ? (
                        <div className="text-center py-12">
                            <div className="inline-flex items-center justify-center w-16 h-16 bg-emerald-100 rounded-full mb-4">
                                <Sparkles className="text-emerald-500" size={32} />
                            </div>
                            <h3 className="text-lg font-bold text-slate-900 mb-2">הכל יציב! 🎉</h3>
                            <p className="text-slate-500 text-sm">
                                לא זוהו שינויים משמעותיים במלאי.
                            </p>
                        </div>
                    ) : activeTab === 'briefing' ? (
                        // --- תדריך בוקר ---
                        <>
                            {/* סיכום מהיר */}
                            {hasBriefingAlerts && (
                                <div className="flex gap-2 overflow-x-auto pb-2 -mx-2 px-2">
                                    {morningSummary.stockout > 0 && (
                                        <div className="flex items-center gap-2 bg-red-100 text-red-700 px-3 py-2 rounded-full text-sm font-bold whitespace-nowrap">
                                            <PackageX size={16} />
                                            <span>{morningSummary.stockout} אזלו</span>
                                        </div>
                                    )}
                                    {morningSummary.restock > 0 && (
                                        <div className="flex items-center gap-2 bg-emerald-100 text-emerald-700 px-3 py-2 rounded-full text-sm font-bold whitespace-nowrap">
                                            <PackagePlus size={16} />
                                            <span>{morningSummary.restock} חזרו</span>
                                        </div>
                                    )}
                                    {morningSummary.trendingUp > 0 && (
                                        <div className="flex items-center gap-2 bg-blue-100 text-blue-700 px-3 py-2 rounded-full text-sm font-bold whitespace-nowrap">
                                            <TrendingUp size={16} />
                                            <span>{morningSummary.trendingUp} עולים</span>
                                        </div>
                                    )}
                                </div>
                            )}

                            {/* רשימת התראות לפי קבוצות */}
                            {Object.entries(groupedBriefingAlerts).map(([type, alerts]) => (
                                alerts.length > 0 && (
                                    <div key={type} className="space-y-2">
                                        <h4 className="text-sm font-bold text-slate-600 flex items-center gap-2">
                                            {ALERT_CONFIG[type]?.title} ({alerts.length})
                                        </h4>
                                        {alerts.map((alert, idx) => (
                                            <BriefingAlertCard key={`${alert.sku}-${idx}`} alert={alert} />
                                        ))}
                                    </div>
                                )
                            ))}

                            {!hasBriefingAlerts && (
                                <div className="text-center py-8 text-slate-500">
                                    <p>אין שינויים מאז הביקור האחרון</p>
                                </div>
                            )}
                        </>
                    ) : (
                        // --- תובנות AI ---
                        <>
                            {hasInsights ? (
                                <div className="space-y-2">
                                    {insights.map((insight, idx) => (
                                        <InsightAlertCard key={idx} insight={insight} />
                                    ))}
                                </div>
                            ) : (
                                <div className="text-center py-8 text-slate-500">
                                    <p>אין שינויים מהקובץ הקודם</p>
                                </div>
                            )}
                        </>
                    )}
                </div>

                {/* כפתורי פעולה */}
                <div className="sticky bottom-0 bg-white border-t border-slate-100 p-4">
                    <div className="flex gap-3">
                        {/* כפתור שיתוף - רק אם יש פריטים שחזרו למלאי */}
                        {activeTab === 'briefing' && groupedBriefingAlerts[ALERT_TYPES.RESTOCK]?.length > 0 && (
                            <button
                                onClick={handleShareRestock}
                                className="h-14 px-6 bg-emerald-500 hover:bg-emerald-600 text-white font-bold rounded-2xl flex items-center justify-center gap-2 transition-all active:scale-[0.98]"
                            >
                                <Share2 size={20} />
                                <span>שתף</span>
                            </button>
                        )}

                        {/* כפתור יצירת דוח - רק בטאב תובנות */}
                        {activeTab === 'insights' && hasInsights && onGenerateReport && (
                            <button
                                onClick={handleGenerateReport}
                                className="h-14 px-6 bg-[#25D366] hover:bg-[#128C7E] text-white font-bold rounded-2xl flex items-center justify-center gap-2 transition-all active:scale-[0.98]"
                            >
                                <MessageCircle size={20} />
                                <span>צור דוח</span>
                            </button>
                        )}

                        <button
                            onClick={onClose}
                            className="flex-1 h-14 bg-slate-900 hover:bg-slate-800 text-white font-bold rounded-2xl flex items-center justify-center gap-2 transition-all active:scale-[0.98]"
                        >
                            <span>המשך לדשבורד</span>
                            <ArrowLeft size={20} />
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default IntelligenceHub;
